(() => {
  if (customElements.get('ai-phrase-generator')) return;

  class AiPhraseGenerator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.endpoint = this.dataset.endpoint;
      this.targetInput = document.getElementById(this.dataset.targetId);
      this.productId = this.dataset.productId;
      this.maxLength = Number.parseInt(this.dataset.maxLength, 10);
      this.trigger = this.querySelector('.ai-phrase-generator__trigger');
      this.modal = this.querySelector('.ai-phrase-modal');
      this.dialog = this.querySelector('.ai-phrase-modal__dialog');
      this.loading = this.querySelector('[data-ai-loading]');
      this.error = this.querySelector('[data-ai-error]');
      this.errorMessage = this.querySelector('[data-ai-error-message]');
      this.results = this.querySelector('[data-ai-results]');
      this.attempts = this.querySelector('[data-ai-attempts]');
      this.regenerateButton = this.querySelector('[data-ai-regenerate]');
      this.viewAllButton = this.querySelector('[data-ai-view-all]');
      this.errorHistoryButton = this.querySelector('[data-ai-error-history]');
      this.appliedMessage = this.querySelector('.ai-phrase-generator__applied');
      this.customRecipient = this.querySelector('[data-ai-custom-recipient]');
      this.customRecipientInput = this.querySelector('[data-ai-custom-recipient-input]');
      this.customRecipientCount = this.querySelector('[data-ai-custom-recipient-count]');
      this.selections = {};
      this.previousPhrases = [];
      this.currentStep = 1;
      this.lastFocusedElement = null;
      this.isGenerating = false;

      if (!this.endpoint || !this.targetInput || !this.productId || !Number.isInteger(this.maxLength)) {
        this.hidden = true;
        return;
      }

      this.loadHistory();
      this.trigger.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-ai-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      this.querySelectorAll('[data-ai-group]').forEach((group) => {
        group.addEventListener('click', (event) => this.selectOption(event, group));
      });
      this.customRecipientInput?.addEventListener('input', () => this.updateCustomRecipient());
      this.querySelectorAll('[data-ai-next]').forEach((button) => {
        button.addEventListener('click', () => this.showStep(Number(button.dataset.aiNext)));
      });
      this.querySelectorAll('[data-ai-back]').forEach((button) => {
        button.addEventListener('click', () => this.showStep(Number(button.dataset.aiBack)));
      });
      this.querySelector('[data-ai-generate]').addEventListener('click', () => this.generate());
      this.regenerateButton.addEventListener('click', () => this.generate());
      this.viewAllButton.addEventListener('click', () => this.showHistory());
      this.errorHistoryButton.addEventListener('click', () => this.showHistory());
      this.querySelector('[data-ai-error-back]').addEventListener('click', () => {
        this.hideError();
        this.showStep(3);
      });
      this.dialog.addEventListener('keydown', (event) => this.handleDialogKeydown(event));
    }

    open() {
      this.lastFocusedElement = document.activeElement;
      this.modal.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ai-phrase-modal-open');
      this.showStep(this.previousPhrases.length ? 4 : this.currentStep);
      requestAnimationFrame(() => this.dialog.focus());
    }

    close() {
      if (this.isGenerating) return;
      this.modal.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ai-phrase-modal-open');
      if (this.lastFocusedElement) this.lastFocusedElement.focus();
    }

    selectOption(event, group) {
      const option = event.target.closest('[data-ai-value]');
      if (!option || !group.contains(option)) return;

      group.querySelectorAll('[data-ai-value]').forEach((item) => {
        const selected = item === option;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', String(selected));
      });

      this.selections[group.dataset.aiGroup] = option.dataset.aiValue;
      const step = group.closest('[data-ai-step]');
      const action = step?.querySelector('[data-ai-next], [data-ai-generate]');

      if (group.dataset.aiGroup === 'destinatario') {
        const isCustomRecipient = option.dataset.aiValue === 'Outra pessoa';
        this.customRecipient.hidden = !isCustomRecipient;
        if (!isCustomRecipient) {
          this.selections.destinatarioDetalhe = '';
          this.customRecipientInput.value = '';
          this.customRecipientCount.textContent = '0/30';
        }
        if (action) action.disabled = isCustomRecipient;
        if (isCustomRecipient) requestAnimationFrame(() => this.customRecipientInput.focus());
        return;
      }

      if (action) action.disabled = false;
    }

    updateCustomRecipient() {
      const value = this.customRecipientInput.value.trim();
      this.selections.destinatarioDetalhe = value;
      this.customRecipientCount.textContent = `${this.customRecipientInput.value.length}/30`;

      const continueButton = this.querySelector('[data-ai-step="1"] [data-ai-next]');
      continueButton.disabled = this.selections.destinatario !== 'Outra pessoa' || value.length < 2;
    }

    showStep(stepNumber) {
      this.currentStep = stepNumber;
      this.hideError();
      this.loading.hidden = true;
      this.querySelectorAll('[data-ai-step]').forEach((step) => {
        step.hidden = Number(step.dataset.aiStep) !== stepNumber;
      });
      this.querySelectorAll('[data-ai-progress]').forEach((item) => {
        const progressStep = Number(item.dataset.aiProgress);
        item.classList.toggle('is-active', stepNumber >= progressStep);
      });
    }

    async generate() {
      if (
        this.isGenerating ||
        !this.selections.destinatario ||
        !this.selections.ocasiao ||
        !this.selections.estilo
      ) {
        return;
      }

      this.isGenerating = true;
      this.hideError();
      this.querySelectorAll('[data-ai-step]').forEach((step) => {
        step.hidden = true;
      });
      this.loading.hidden = false;

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            visitorId: this.getVisitorId(),
            productId: this.productId,
            destinatario: this.selections.destinatario,
            destinatarioDetalhe: this.selections.destinatarioDetalhe || '',
            ocasiao: this.selections.ocasiao,
            estilo: this.selections.estilo,
            maxLength: this.maxLength,
            frasesAnteriores: this.previousPhrases.slice(-10)
          })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(data.message || 'Não foi possível gerar as frases.');
          error.code = data.error;
          error.attemptsRemaining = data.attemptsRemaining;
          throw error;
        }

        if (!Array.isArray(data.phrases) || data.phrases.length !== 5) {
          throw new Error('O serviço retornou uma resposta incompleta. Tente novamente.');
        }

        this.previousPhrases = [...new Set([...this.previousPhrases, ...data.phrases])].slice(-15);
        this.savedAttemptsRemaining = data.attemptsRemaining;
        this.saveHistory();
        this.renderResults(data.phrases);
        this.viewAllButton.hidden = this.previousPhrases.length <= 5;
        this.updateAttempts(data.attemptsRemaining);
        this.showStep(4);
      } catch (error) {
        this.showError(error.message);
        if (error.code === 'generation_limit_reached' || error.attemptsRemaining === 0) {
          this.savedAttemptsRemaining = 0;
          this.saveHistory();
          this.regenerateButton.hidden = true;
          this.errorHistoryButton.hidden = this.previousPhrases.length === 0;
        }
      } finally {
        this.isGenerating = false;
        this.loading.hidden = true;
      }
    }

    renderResults(phrases) {
      this.results.replaceChildren();

      phrases.forEach((phrase) => {
        const button = document.createElement('button');
        const text = document.createElement('span');
        const action = document.createElement('span');

        button.type = 'button';
        button.className = 'ai-phrase-result';
        text.className = 'ai-phrase-result__text';
        text.textContent = phrase;
        action.className = 'ai-phrase-result__action';
        action.textContent = 'Usar esta frase';

        button.append(text, action);
        button.addEventListener('click', () => this.applyPhrase(phrase));
        this.results.append(button);
      });
    }

    showHistory() {
      if (!this.previousPhrases.length) return;
      this.renderResults(this.previousPhrases);
      this.viewAllButton.hidden = true;
      this.showStep(4);
      this.updateAttempts(this.savedAttemptsRemaining);
    }

    applyPhrase(phrase) {
      if (!this.targetInput || phrase.length > this.maxLength) return;

      this.targetInput.value = phrase;
      this.targetInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.targetInput.dispatchEvent(new Event('change', { bubbles: true }));

      this.appliedMessage.textContent = `Frase aplicada: “${phrase}”`;
      this.appliedMessage.hidden = false;
      this.close();
      this.targetInput.focus();
    }

    updateAttempts(remaining) {
      const value = Number.isInteger(remaining) ? remaining : 0;
      if (value <= 0) {
        this.attempts.textContent = 'Você utilizou as 3 gerações disponíveis para este produto.';
        this.regenerateButton.hidden = true;
        return;
      }

      this.attempts.textContent = `${value} ${value === 1 ? 'geração restante' : 'gerações restantes'}.`;
      this.regenerateButton.hidden = false;
    }

    showError(message) {
      this.querySelectorAll('[data-ai-step]').forEach((step) => {
        step.hidden = true;
      });
      this.errorMessage.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      this.error.hidden = true;
      this.errorMessage.textContent = '';
      this.errorHistoryButton.hidden = true;
    }

    loadHistory() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.getHistoryKey()) || 'null');
        const isValid =
          stored &&
          Array.isArray(stored.phrases) &&
          Number.isFinite(stored.createdAt) &&
          Date.now() - stored.createdAt < 86_400_000;

        if (!isValid) {
          localStorage.removeItem(this.getHistoryKey());
          return;
        }

        this.previousPhrases = stored.phrases
          .filter((phrase) => typeof phrase === 'string' && phrase.length <= this.maxLength)
          .slice(-15);
        this.savedAttemptsRemaining = Number.isInteger(stored.attemptsRemaining)
          ? stored.attemptsRemaining
          : Math.max(0, 3 - Math.ceil(this.previousPhrases.length / 5));

        if (this.previousPhrases.length) {
          this.renderResults(this.previousPhrases);
          this.viewAllButton.hidden = true;
          this.updateAttempts(this.savedAttemptsRemaining);
          this.currentStep = 4;
        }
      } catch {
        // O gerador continua funcionando mesmo se o armazenamento estiver indisponível.
      }
    }

    saveHistory() {
      if (!this.previousPhrases.length) return;

      try {
        const existing = JSON.parse(localStorage.getItem(this.getHistoryKey()) || 'null');
        localStorage.setItem(
          this.getHistoryKey(),
          JSON.stringify({
            phrases: this.previousPhrases.slice(-15),
            attemptsRemaining: this.savedAttemptsRemaining,
            createdAt: Number.isFinite(existing?.createdAt) ? existing.createdAt : Date.now()
          })
        );
      } catch {
        // O limite continua protegido pelo backend mesmo sem histórico local.
      }
    }

    getHistoryKey() {
      return `hajdasz_ai_phrase_history_${this.productId}`;
    }

    getVisitorId() {
      const storageKey = 'hajdasz_ai_phrase_visitor';
      let visitorId = localStorage.getItem(storageKey);

      if (!visitorId) {
        visitorId =
          typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID().replaceAll('-', '')
            : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random()
                .toString(36)
                .slice(2)}`;
        localStorage.setItem(storageKey, visitorId);
      }

      return visitorId;
    }

    handleDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('button:not([disabled]):not([hidden])')].filter(
        (element) => element.offsetParent !== null
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('ai-phrase-generator', AiPhraseGenerator);
})();
